// src/services/websocketService.js
// Manages the WebSocket connection and provides subscription capabilities
// --- UPDATE (live reliability) -------------------------------------------------
// • Reconnects with exponential backoff + jitter (1 s → 30 s) and never gives up permanently:
//   after the attempt budget it waits for the browser to come back online or the tab to become
//   visible, then starts again. Before, 10 failed attempts (≈50 s of a Wi-Fi drop) left every
//   live dashboard frozen until a full page reload.
// • Handlers of a replaced socket are ignored (a late onclose from the old socket used to mark
//   the new, healthy connection as disconnected).
// • disconnect() is final until connect() is called again (no surprise reconnect after logout).
// • subscribe() returns an unsubscribe function (unsubscribe() still works as before).
// • status / getStatus() for UI indicators; debug logging only when
//   localStorage.flowsense_ws_debug = '1' (every subscribe/unsubscribe used to log).
// --- END UPDATE ------------------------------------------------------------------

import { config } from '../config'; // Import frontend config
import { getAuthToken, clearAuthData } from '../utils/auth'; // Import auth utils

// --- UPDATE ---
const debugEnabled = () => {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem('flowsense_ws_debug') === '1'; } catch (_) { return false; }
};
const dlog = (...a) => { if (debugEnabled()) console.log(...a); };
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
// --- END UPDATE ---

class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.messageListeners = {}; // Store listeners by message type
    this.globalListeners = []; // Store global listeners
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // Attempts per burst before waiting for online/visible
    this.reconnectDelay = 5000; // kept for compatibility (backoff below is used)
    this.reconnectTimer = null;
    // --- UPDATE ---
    this.status = 'idle'; // idle | connecting | connected | reconnecting | disconnected | waiting
    this.manualClose = false;
    this.lastMessageAt = null;
    this._wakeBound = false;
    // --- END UPDATE ---
  }

  // --- UPDATE ---
  getStatus() {
    return { status: this.status, isConnected: this.isConnected, reconnectAttempts: this.reconnectAttempts, lastMessageAt: this.lastMessageAt };
  }

  _setStatus(status, extra = {}) {
    this.status = status;
    this._notifyListeners({ type: 'status', status, ...extra });
  }

  /** Resume after the attempt budget is spent: when the network returns or the tab is shown. */
  _bindWake() {
    if (this._wakeBound || typeof window === 'undefined') return;
    this._wakeBound = true;
    const wake = () => {
      if (this.manualClose || this.isConnected || this.isConnecting) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      if (this.status === 'waiting' || this.status === 'disconnected' || this.status === 'reconnecting') {
        this.reconnectAttempts = 0;
        if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
        this.connect();
      }
    };
    window.addEventListener('online', wake);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', wake);
  }

  _backoffMs() {
    const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * (2 ** Math.max(0, this.reconnectAttempts - 1)));
    return Math.round(exp / 2 + Math.random() * (exp / 2)); // "equal jitter"
  }
  // --- END UPDATE ---

  // Connect to the WebSocket server
  connect() {
    // --- UPDATE ---
    this.manualClose = false;
    this._bindWake();
    // --- END UPDATE ---
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      dlog("[WS Service] Already connected or connecting.");
      return;
    }

    if (this.isConnecting) {
      dlog("[WS Service] Connection attempt already in progress.");
      return;
    }

    this.isConnecting = true;
    dlog(`[WS Service] Attempting to connect to ${config.websocketUrl}`);
    // --- UPDATE ---
    if (this.status !== 'reconnecting') this.status = 'connecting';
    // --- END UPDATE ---

    let socket;
    try {
      socket = new WebSocket(config.websocketUrl);
    } catch (e) {
      // invalid URL / blocked — schedule like a failed attempt
      console.error('[WS Service] Could not create WebSocket:', e);
      this.isConnecting = false;
      this._scheduleReconnect('create_failed');
      return;
    }
    this.ws = socket;

    socket.onopen = () => {
      if (this.ws !== socket) return; // --- UPDATE: replaced socket
      dlog("[WS Service] Connected.");
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

      // Send authentication message after connection is open
      const authToken = getAuthToken();
      if (authToken) {
        dlog("[WS Service] Sending authentication message.");
        this.send({ type: 'auth', token: authToken });
      } else {
        console.warn("[WS Service] No auth token available to send.");
      }

      this._setStatus('connected');
      // Notify components listening to raw lifecycle events
      this._emit('connect');
    };

    socket.onmessage = (event) => {
      if (this.ws !== socket) return; // --- UPDATE
      this.lastMessageAt = Date.now();
      try {
        const message = JSON.parse(event.data);

        // Notify specific listeners by message type
        if (message.type && this.messageListeners[message.type]) {
          // copy: a listener may unsubscribe itself while we iterate
          Array.from(this.messageListeners[message.type]).forEach(listener => {
            try { listener(message); } catch (e) { console.error(`[WS Service] Error in listener for type ${message.type}:`, e); }
          });
        }

        // Notify global listeners
        this.globalListeners.slice().forEach(listener => {
          try { listener(message); } catch (e) { console.error("[WS Service] Error in global listener:", e); }
        });

        if (message.type === 'error' && message.message === 'Invalid token') {
          console.error("[WS Service] Received Invalid token error from backend. Clearing auth data.");
          clearAuthData();
          this.disconnect(1000, "Invalid token received");
        }

      } catch (error) {
        console.error("[WS Service] Error parsing or processing message:", event.data, error);
      }
    };

    socket.onerror = (error) => {
      if (this.ws !== socket) return; // --- UPDATE
      console.error('[WS Service] WebSocket Error:', error);
      this.isConnected = false;
      this.isConnecting = false;
      this._notifyListeners({ type: 'status', status: 'error', error: error });
    };

    socket.onclose = (event) => {
      if (this.ws !== socket) return; // --- UPDATE: a replaced socket closing is not our state
      dlog(`[WS Service] WebSocket Closed. Code: ${event.code}, Reason: ${event.reason || 'N/A'}`);
      this.isConnected = false;
      this.isConnecting = false;
      this.ws = null;
      this._setStatus('disconnected', { code: event.code, reason: event.reason });
      // Notify components listening to raw lifecycle events
      this._emit('disconnect', event.reason);

      const authRejected = event.code === 1008 || event.reason === 'Invalid token';
      if (!this.manualClose && event.code !== 1000 && !authRejected) {
        this._scheduleReconnect(event.reason || `code ${event.code}`);
      }
    };
  }

  // --- UPDATE ---
  _scheduleReconnect(reason) {
    if (this.manualClose) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`[WS Service] ${this.reconnectAttempts} reconnect attempts failed — waiting for the network or the tab to come back.`);
      this.status = 'waiting';
      this._notifyListeners({ type: 'status', status: 'failed', reason: 'Max reconnect attempts reached — will retry when online' });
      // keep trying slowly in the background as well
      this.reconnectTimer = setTimeout(() => { this.reconnectAttempts = 0; this.connect(); }, MAX_DELAY_MS * 2);
      return;
    }
    this.reconnectAttempts++;
    const delay = this._backoffMs();
    dlog(`[WS Service] Reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay} ms (${reason})`);
    this._setStatus('reconnecting', { attempt: this.reconnectAttempts, delayMs: delay });
    this._emit('reconnecting');
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }
  // --- END UPDATE ---

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
        return true;
      } catch (e) {
        console.error("[WS Service] Error sending message:", e);
      }
    } else {
      dlog("[WS Service] WebSocket is not open. Message not sent:", message);
    }
    return false;
  }

  disconnect(code = 1000, reason = 'Client disconnecting') {
    // --- UPDATE: final until connect() is called again ---
    this.manualClose = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    const socket = this.ws;
    this.ws = null;
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      dlog(`[WS Service] Disconnecting with code ${code}, reason: ${reason}`);
      try { socket.close(code, reason); } catch (_) { /* already closing */ }
    }
    // --- END UPDATE ---
    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this._setStatus('disconnected', { code: code, reason: reason });
  }

  /**
   * Subscribe to messages of one type.
   * @returns {function} unsubscribe
   */
  subscribe(messageType, listener) {
    if (!this.messageListeners[messageType]) {
      this.messageListeners[messageType] = new Set();
    }
    this.messageListeners[messageType].add(listener);
    dlog(`[WS Service] Subscribed listener for type: ${messageType}`);
    return () => this.unsubscribe(messageType, listener);
  }

  unsubscribe(messageType, listener) {
    if (this.messageListeners[messageType]) {
      this.messageListeners[messageType].delete(listener);
      if (this.messageListeners[messageType].size === 0) {
        delete this.messageListeners[messageType];
      }
      dlog(`[WS Service] Unsubscribed listener for type: ${messageType}`);
    }
  }

  subscribeGlobal(listener) {
    this.globalListeners.push(listener);
    dlog("[WS Service] Subscribed global listener.");
    return () => this.unsubscribeGlobal(listener);
  }

  unsubscribeGlobal(listener) {
    this.globalListeners = this.globalListeners.filter(l => l !== listener);
    dlog("[WS Service] Unsubscribed global listener.");
  }

  _notifyListeners(event) {
    dlog("[WS Service] Status Update:", event.status);
    if (this.messageListeners['status']) {
      Array.from(this.messageListeners['status']).forEach(listener => {
        try { listener(event); } catch (e) { console.error(`[WS Service] Error in status listener:`, e); }
      });
    }
  }

  // =========================================================================
  // ENTERPRISE FIX: LIFECYCLE ALIASES
  // Exposes standard .on() and .off() alias methods so the NotificationContext
  // can natively listen to raw socket connection events without rewriting logic.
  // =========================================================================
  on(event, callback) {
      if (event === 'connect' || event === 'disconnect' || event === 'reconnecting') {
          // Store connection lifecycle events in a special namespace to avoid data payload conflicts
          return this.subscribe(`lifecycle_${event}`, callback);
      }
      return this.subscribe(event, callback);
  }

  off(event, callback) {
      if (event === 'connect' || event === 'disconnect' || event === 'reconnecting') {
          this.unsubscribe(`lifecycle_${event}`, callback);
      } else {
          this.unsubscribe(event, callback);
      }
  }

  _emit(event, ...args) {
      const targetList = this.messageListeners[`lifecycle_${event}`];
      if (targetList) {
          Array.from(targetList).forEach(listener => {
              try { listener(...args); } catch (e) { console.error(`[WS Service] Error in ${event} emit:`, e); }
          });
      }
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;
